import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowRight } from 'lucide-react';
import { confidencePercent } from '../../../utils/detailFormat.js';

function riskBadgeVariant(riskLevel) {
  switch (String(riskLevel ?? '').toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}

function truncate(value, max = 28) {
  const text = String(value ?? '');
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

/**
 * Compact current → proposed preview for inbox rows and card view.
 * Mirrors Train Me meta diff but tuned for interim proposal list rows.
 */
function ProposalPreview({ event, showSignals = true }) {
  const { t } = useTranslation('work-queues-app');
  const detail = event.detail ?? {};
  const current = detail.currentStatement ?? null;
  const proposed = detail.proposedStatement ?? event.situation ?? null;
  const confidence = confidencePercent(detail.confidence);
  const evidenceCount = detail.evidenceCount ?? event.commentsCount ?? 0;

  if (!current && !proposed) return null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {current && proposed ? (
        <div className="flex min-w-0 items-center gap-1.5 text-[11px]">
          <span className="min-w-0 truncate font-mono text-content-faint line-through">
            {truncate(current)}
          </span>
          <ArrowRight size={12} className="shrink-0 text-content-faint" aria-hidden />
          <span className="min-w-0 truncate font-mono text-amber-500">{truncate(proposed)}</span>
        </div>
      ) : proposed ? (
        <p className="line-clamp-2 text-[11px] leading-snug text-content-secondary">{proposed}</p>
      ) : null}

      {showSignals && (confidence || detail.riskLevel || evidenceCount > 0) && (
        <div className="flex flex-wrap items-center gap-1">
          {confidence && (
            <Badge variant="informative" size="small">
              {t('proposal.confidence')}: {confidence}
            </Badge>
          )}
          {detail.riskLevel && (
            <Badge variant={riskBadgeVariant(detail.riskLevel)} size="small">
              {detail.riskLevel}
            </Badge>
          )}
          {evidenceCount > 0 && (
            <Badge variant="neutral" size="small">
              {t('proposal.evidenceCount')}: {evidenceCount}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default ProposalPreview;
